import { getDisplayNameFromEntityType } from '@/modules/entities/utils/getDisplayNameFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { sumHoldingsGroupCurrentValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useEntitySyncingState } from '@/modules/estate/hooks/useEntitySyncingState';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

/**
 * Extracts grantor names from different trust types, handling various grantor structures
 */
export const extractTrustGrantorNames = (
  trust: EstatePage_EntityFragment
): string[] => {
  const subtype = trust.subtype;

  // Handle single grantor trusts (SLATTrust)
  if ('grantor' in subtype && subtype.grantor?.individual?.displayName) {
    return [subtype.grantor.individual.displayName];
  }

  // Handle multiple grantor trusts (RevocableTrust, IrrevocableTrust, QPRTTrust, CRTTrust, CLTTrust)
  if (
    'grantors' in subtype &&
    subtype.grantors &&
    Array.isArray(subtype.grantors)
  ) {
    return subtype.grantors
      .map((grantor) => grantor?.individual?.displayName)
      .filter((name): name is string => Boolean(name));
  }

  // For other trust types that don't have explicit grantor fields
  // (like GRATTrust, ILITTrust), return empty array
  return [];
};
/**
 * Formats the complete trust subtitle in the format: net value | grantor(s) | trust type
 */
export const useFormattedTrustSubtitle = (
  trust: EstatePage_EntityFragment
): string => {
  const doesEntityHaveActiveSync = useEntitySyncingState(trust.id);

  const grantorNames = extractTrustGrantorNames(trust);

  const currentValue = sumHoldingsGroupCurrentValue([trust]);

  const parts: string[] = [];

  // Add net value first, or loading if there is an active sync
  if (doesEntityHaveActiveSync) {
    parts.push('Syncing...');
  } else {
    parts.push(formatCurrencyNoDecimals(currentValue));
  }

  // Add grantor names (or fallback if none available)
  if (grantorNames.length > 0) {
    parts.push(grantorNames.join(' & '));
  }

  const trustType = getDisplayNameFromEntityType(
    getEntityTypeFromEntityKind(trust.kind)
  );
  parts.push(trustType);

  return parts.join(' | ');
};
